import React, {
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    TickerAttachmentPreview,
    UnfurledLinkAttachmentPreview,
} from '@components/EmbeddedMessageAttachement/EditorAttachmentPreview'
import { ReplyToMessageContext } from '@components/ReplyToMessageContext/ReplyToMessageContext'
import {
    EmbeddedMessageAttachment,
    MessageType,
    TickerAttachment,
    UnfurledLinkAttachment,
} from '@towns-protocol/sdk'
import {
    getPointAfter,
    getPointBeforeLocation,
    insertText,
    moveSelection,
    resetEditor,
    TElement,
} from '@udecode/plate-common'
import { Plate, TPlateEditor } from '@udecode/plate-common/react'
import { Box, BoxProps, Checkbox, Paragraph, Stack } from '@ui'
import { isMacOS, useDevice } from 'hooks/useDevice'
import { LoadingUnfurledLinkAttachment } from 'hooks/useExtractInternalLinks'
import { noop } from 'lodash-es'
import { useInputStore, useStore } from 'store/store'
import { Channel, LookupUserFn, SendTextMessageOptions } from 'use-towns-client'

import { CaptureLinkAttachmentsPlugin } from './components/CaptureLinkAttachmentsPlugin'
import { EditorToolbarBottom } from './components/EditorToolbarBottom'
import { EditorToolbarTop } from './components/EditorToolbarTop'
import { PasteFilePlugin } from './components/PasteFilePlugin'
import {
    TComboboxItemWithData,
    TMentionTicker,
    TUserIDNameMap,
} from './components/plate-ui/autocomplete/types'
import { Editor } from './components/plate-ui/Editor'
import { RichTextPlaceholder } from './components/RichTextPlaceholder'
import { SendMarkdownPlugin } from './components/SendMarkdownPlugin'
import { TGetUserMentions } from './hooks/editorHooks'
import { loadMissingLanguages } from './plugins/LazyPrismPlugin'
import { OnFocusPlugin } from './plugins/OnFocusPlugin'
import { RememberInputPlugin } from './plugins/RememberInputPlugin'
import { deserializeMd } from './utils/deserializeMD'
import { focusEditorTowns, isInputFocused } from './utils/helpers'
import { toMD } from './utils/toMD'
import { toPlainText } from './utils/toPlainText'
import createTownsEditor from './plugins'
import {
    clearComposerBridgeActions,
    ComposerBridgeActions,
    setComposerBridgeActions,
} from './useComposerBridge'

const EMPTY_NODE: TElement = {
    id: '1',
    type: 'p',
    children: [{ text: '' }],
}

type RichTextEditorProps = {
    tabIndex?: number
    threadId?: string
    threadPreview?: string
    editable?: boolean
    hasInlinePreview?: boolean
    displayButtons?: 'always' | 'on-focus'
    placeholder?: string
    fileCount?: number
    editing?: boolean
    autoFocus?: boolean
    storageId?: string
    background?: BoxProps['background']
    initialValue?: string
    /* hashmap of user `displayName` and `userId`. Used to convert pasted text into `mention` node */
    userHashMap: TUserIDNameMap
    /* users shown in @mention popup while typing */
    getUserMentions: TGetUserMentions
    /* channels shown in #channel popup while typing */
    channelMentions: TComboboxItemWithData<Channel>[]
    /* list of all channels in space */
    channels: Channel[]
    /* func to get user details by `userId`. Generally provided by `useUserLookupContext()` */
    lookupUser: LookupUserFn
    /* props related to attachment handling */
    unfurledLinkAttachments?: (UnfurledLinkAttachment | LoadingUnfurledLinkAttachment)[]
    onRemoveUnfurledLinkAttachment?: (attachmentId: string) => void
    onMessageLinksUpdated?: (
        messages: EmbeddedMessageAttachment[] | undefined,
        links: (UnfurledLinkAttachment | LoadingUnfurledLinkAttachment)[] | undefined,
    ) => void
    tickerAttachments?: TickerAttachment[]
    onSelectTicker?: (ticker: TMentionTicker) => void
    onRemoveTicker?: (address: string, chain: string) => void
    onInsertAddress?: (address: string, chain: string) => void
    onRemoveAddress?: (address: string, chain: string) => void
    /* count of attachments remaining after removal */
    remainingAttachmentCount?: number
    /* callback invoked when user presses enter key or click send button  */
    onSend?: (
        message: string,
        options: Pick<
            SendTextMessageOptions,
            'mentions' | 'messageType' | 'emptyMessage' | 'appClientAddress' | 'slashCommand'
        >,
    ) => void
    onCancel?: () => void
    onChange?: (editor: TPlateEditor) => void
    onArrowEscape?: (direction: 'up' | 'down') => void
    onCancelInlineReply?: () => void
    renderSendButton?: (onSend: () => void) => React.ReactNode
    renderTradingBottomBar?: React.ReactNode
    allowEmptyMessage?: boolean
    hasThreadReplies?: boolean
    exposeComposerBridge?: boolean
}

export const RichTextEditor = ({
    tabIndex,
    threadId,
    threadPreview,
    editable = true,
    hasInlinePreview = false,
    displayButtons = 'always',
    placeholder = 'Write something ...',
    fileCount = 0,
    editing: isEditing = false,
    autoFocus: _autoFocus = true,
    storageId: _storageId,
    background = 'level2',
    initialValue: _initialValue,
    userHashMap,
    getUserMentions,
    channelMentions,
    tickerAttachments = undefined,
    channels,
    lookupUser,
    unfurledLinkAttachments = undefined,
    onArrowEscape,
    onRemoveUnfurledLinkAttachment = noop,
    onMessageLinksUpdated = noop,
    onSelectTicker = noop,
    onInsertAddress = noop,
    onRemoveAddress = noop,
    onRemoveTicker = noop,
    onCancel = noop,
    onCancelInlineReply = noop,
    onSend = noop,
    renderSendButton,
    renderTradingBottomBar,
    allowEmptyMessage = false,
    remainingAttachmentCount = 0,
    hasThreadReplies = false,
    exposeComposerBridge = false,
}: RichTextEditorProps) => {
    /* refs */
    const { isTouch, isAndroid } = useDevice()
    const storageId = useRef(_storageId ?? 'editor')
    const editableContainerRef = useRef<HTMLDivElement>(null)

    /* state */
    const [isSendingMessage, setIsSendingMessage] = useState(false)
    const [focused, setFocused] = useState(false)
    const [editorText, setEditorText] = useState('')
    const [isEditorEmpty, setIsEditorEmpty] = useState(true)
    const [isFormattingToolbarOpen, setIsFormattingToolbarOpen] = useState(false)
    const [isOtherInputFocused] = useState(isInputFocused)

    const valueFromStore = useInputStore((state) => state.channelMessageInputMap[storageId.current])
    const setInput = useInputStore((state) => state.setChannelmessageInput)

    const {
        replyToEventId,
        canReplyInThread,
        replyMode,
        setReplyMode: setReplyMode,
    } = useContext(ReplyToMessageContext)

    const showThreadCheckbox = hasInlinePreview && canReplyInThread

    const userMentionsRef = useRef(getUserMentions)
    userMentionsRef.current = getUserMentions

    const channelMentionsRef = useRef(channelMentions)
    channelMentionsRef.current = channelMentions

    const [editor] = useState<TPlateEditor>(() => {
        const initialValue = (() => {
            /* used when editing a message: convert initial value passed as prop from MD string to Plate JSON  */
            if (_initialValue) {
                return deserializeMd(_initialValue, channels, userHashMap, lookupUser)
            }
            /* used to restore a draft message from local storage */
            if (editable && valueFromStore && valueFromStore.trim().length > 0) {
                return deserializeMd(valueFromStore, channels, userHashMap, lookupUser)
            }
            /* If all else fails, set Plate JSON as default empty string  */
            return [{ ...EMPTY_NODE }]
        })()

        return createTownsEditor(
            storageId.current,
            channels,
            userHashMap,
            userMentionsRef.current,
            () => channelMentionsRef.current,
            initialValue,
            lookupUser,
            onSelectTicker,
            onInsertAddress,
            onRemoveAddress,
        )
    })

    useEffect(() => {
        if (!exposeComposerBridge) {
            return
        }

        const actions: ComposerBridgeActions = {
            insertText: (text: string, embeds?: string[]) => {
                insertText(editor, text)
                embeds?.forEach((embed) => {
                    insertText(editor, embed)
                })
                moveSelection(editor, { unit: 'offset' })
                focusEditorTowns(editor, true)
            },
        }

        setComposerBridgeActions(actions)
        return () => {
            clearComposerBridgeActions(actions)
        }
    }, [editor, exposeComposerBridge])

    const disabled = useMemo(() => !editable || isSendingMessage, [editable, isSendingMessage])
    const disabledSend = useMemo(() => typeof onSend !== 'function', [onSend])

    /**
     * For Android devices, we need to show a placeholder as an absolute positioned JSX element
     * because the native PlateJS placeholder causes issues with @mentions and #channels, where the
     * caret position is moved to the left when user types a mention or channel
     *
     * For other devices, we just need to pass the placeholder string to the PlateJS <Editor /> component
     */
    const showAndroidPlaceholder = useMemo(
        () => isAndroid() && editorText.length === 0,
        [isAndroid, editorText],
    )

    // Determine if we should auto-focus based on props and context
    const autoFocus = useMemo(
        () => (isTouch && hasInlinePreview) || isEditing || _autoFocus,
        [isTouch, hasInlinePreview, _autoFocus, isEditing],
    )

    // Handle focus based on autoFocus prop or reply initiation
    useEffect(() => {
        if (editor && !isInputFocused() && (autoFocus || replyToEventId)) {
            focusEditorTowns(editor, true)
        }
    }, [autoFocus, replyToEventId, editor])

    // Lazy load Prism languages for code blocks in initial content (edit mode)
    // forceRedecorate=true ensures syntax highlighting appears even if languages are already loaded
    useEffect(() => {
        loadMissingLanguages(editor, true)
    }, [editor])

    /** Reset the editor after sending a message and clear local storage value as well */
    const resetEditorAfterSend = useCallback(() => {
        setInput(storageId.current, '')
        if (editor) {
            resetEditor(editor)
            // Delay focusing the editor to wait for the editor to reset and re-render
            setTimeout(() => {
                focusEditorTowns(editor, !isEditing || isTouch)
            }, 100)
        }
        setIsSendingMessage(false)
    }, [setInput, editor, isEditing, isTouch])

    const onFocusChange = useCallback((focus: boolean) => {
        setFocused(focus)
    }, [])

    const onFocus = useCallback(() => onFocusChange(true), [onFocusChange])

    /**
     * We want to delay the `onBlur` event to allow the user to click on the formatting toolbar icon
     * Without this delay, the bottom toolbar would disappear before `isFormattingToolbarOpen` is set to `true`
     */
    const onBlur = useCallback(() => {
        if (isTouch) {
            setTimeout(() => onFocusChange(false), 0)
        }
    }, [isTouch, onFocusChange])

    const onChangeHandler = useCallback(() => {
        /**
         * `editorText` is used to check if the editor is empty or not and display the placeholder
         * on mobile devices, this is often slow because of the way the editor is rendered and
         * its children are updated.
         *
         * As a workaround, we set the editor text to a space character to remove the placeholder
         * on any manual input change. The reconciliation of the actual editor text is completed later
         */
        if (editor) {
            startTransition(() => {
                const text = toPlainText(editor.children)
                setEditorText(text)
                setIsEditorEmpty(text.length === 0)
            })
        }
    }, [editor])

    /**
     * Handle thread checkbox click to maintain editor focus, similar to toolbar buttons
     * This prevents the editor from losing focus when toggling the "send to thread" checkbox
     */
    const handleRefocus = useCallback(
        (event: React.MouseEvent<HTMLInputElement>) => {
            // Focus the editor after the state change to maintain focus
            setTimeout(() => {
                focusEditorTowns(editor)
            }, 0)
        },
        [editor],
    )

    const sendMessage = useCallback(async () => {
        if (!editor) {
            return
        }
        if (editorText.length > 0 || fileCount > 0 || remainingAttachmentCount > 0) {
            const { message, mentions, slashCommand } = await toMD(editor)
            onSend(message, {
                messageType: MessageType.Text,
                mentions: mentions.length > 0 ? mentions : undefined,
                appClientAddress: slashCommand?.appClientAddress,
                slashCommand: slashCommand
                    ? {
                          value: slashCommand.value,
                          botName: slashCommand.command.botName,
                          appAddress: slashCommand.appAddress,
                          appClientAddress: slashCommand.appClientAddress,
                      }
                    : undefined,
            })
            resetEditorAfterSend()
        } else if (allowEmptyMessage) {
            onSend('', {
                messageType: MessageType.Text,
                emptyMessage: true,
            })
            resetEditorAfterSend()
        }
    }, [
        allowEmptyMessage,
        editor,
        editorText.length,
        fileCount,
        remainingAttachmentCount,
        onSend,
        resetEditorAfterSend,
    ])

    // Update the editor text in the component state one time when the editor is initialized
    // After the editor is initialized, the editor text is updated in the `onChange` handler
    useEffect(() => {
        onChangeHandler()
    }, [onChangeHandler])

    /**
     * When the user presses the arrow keys, we want to exit the editor if the cursor is at the top or bottom
     * of the editor. This is to allow the user to navigate to the previous or next message in the thread
     */
    const exitEditorOnArrow: React.KeyboardEventHandler = useCallback(
        (event) => {
            // console.log('exitEditorOnArrow called', {
            //     onArrowEscape: !!onArrowEscape,
            //     editor: !!editor,
            //     selection: !!editor?.selection,
            //     key: event.key,
            // })
            if (!onArrowEscape || !editor || !editor.selection) {
                return
            }

            const isAtBeginning = !getPointBeforeLocation(editor, editor.selection)
            // console.log('Cursor position check', { isAtBeginning, key: event.key })

            if (event.key === 'ArrowUp' && (isAtBeginning || isEditorEmpty)) {
                // console.log('Calling onArrowEscape with direction up', {
                //     isAtBeginning,
                //     isEditorEmpty,
                // })
                onArrowEscape('up')
            } else if (event.key === 'ArrowDown' && !getPointAfter(editor, editor.selection)) {
                // console.log('Calling onArrowEscape with direction down')
                onArrowEscape('down')
            }
        },
        [editor, onArrowEscape, isEditorEmpty],
    )

    /**
     * We need to do this for Android ONLY, to prevent the placeholder from showing up while typing,
     * since soft keyboard `onChange` events are not quick enough, and leads to overlapping placeholders
     * for a brief moment.
     *
     * In this case, we set the editor text to a space character to remove the placeholder on any manual input change
     *
     * For other devices, we don't need to do this, as the placeholder is handled by the PlateJS library
     * @see showAndroidPlaceholder
     */
    const hideAndroidPlaceholder = useCallback(() => {
        if (editorText.length === 0 && isAndroid()) {
            setEditorText(' ')
        }
    }, [editorText, isAndroid])

    const sendWithCmdEnter = useStore((state) => state.sendWithCmdEnter)
    const isDarwin = isMacOS()

    const customKeydownHandler: React.KeyboardEventHandler = useCallback(
        async (event) => {
            /** On desktop, we want to exit the editor if the user presses up/down  */
            if (!isTouch && event.key.includes('Arrow')) {
                // console.log('Calling exitEditorOnArrow from customKeydownHandler')
                exitEditorOnArrow(event)
            }

            const { key, metaKey, ctrlKey, shiftKey } = event
            const modifierPressed = isDarwin ? metaKey : ctrlKey

            if (!editor || isTouch || disabledSend || disabled) {
                return
            }

            if (key === 'Escape' && onCancelInlineReply !== noop) {
                if (focused) {
                    event.preventDefault()
                    onCancelInlineReply()
                }
                return
            }

            if (key === 'Enter') {
                if (shiftKey) {
                    // shift+enter should create a new line
                    return
                }

                if (isEditorEmpty && fileCount === 0 && remainingAttachmentCount === 0) {
                    return
                }

                if (sendWithCmdEnter) {
                    if (modifierPressed) {
                        event.preventDefault()
                        await sendMessage()
                    }
                } else {
                    event.preventDefault()
                    await sendMessage()
                }
            }
        },
        [
            isTouch,
            editor,
            disabledSend,
            disabled,
            focused,
            isEditorEmpty,
            fileCount,
            remainingAttachmentCount,
            isDarwin,
            sendWithCmdEnter,
            exitEditorOnArrow,
            sendMessage,
            onCancelInlineReply,
        ],
    )

    const sendButtons = (
        <SendMarkdownPlugin
            displayButtons={displayButtons ?? 'on-focus'}
            disabled={disabled || disabledSend}
            focused={focused}
            isEditing={isEditing ?? false}
            hasImage={fileCount > 0 || remainingAttachmentCount > 0}
            key="markdownplugin"
            isEditorEmpty={isEditorEmpty}
            sendMessage={sendMessage}
            renderSendButton={renderSendButton}
            onCancel={onCancel}
        />
    )

    // force show floating toolbar in desktop when user selects text in editor
    const toolbarTop = useMemo(() => {
        return (
            <EditorToolbarTop
                readOnly={!editable}
                focused={focused || !isEditorEmpty}
                editing={isEditing}
                background={background}
                threadId={threadId}
                threadPreview={threadPreview}
                showFormattingToolbar={isFormattingToolbarOpen}
                setIsFormattingToolbarOpen={setIsFormattingToolbarOpen}
            />
        )
    }, [
        editable,
        background,
        focused,
        isEditorEmpty,
        isEditing,
        isFormattingToolbarOpen,
        threadId,
        threadPreview,
    ])

    return (
        <Plate editor={editor} key={`plate-${storageId.current}`} onValueChange={onChangeHandler}>
            {!isTouch && toolbarTop}
            <Box position="relative">
                <Stack horizontal width="100%" paddingRight="sm" alignItems="end">
                    <Box grow paddingX="md" position="relative" ref={editableContainerRef}>
                        <Editor
                            data-testid="send-message-text-box"
                            readOnly={!editable}
                            autoFocus={autoFocus && !isOtherInputFocused}
                            tabIndex={tabIndex}
                            disabled={isSendingMessage}
                            placeholder={replyToEventId ? 'Send a reply...' : placeholder}
                            isEditing={isEditing}
                            hideAndroidPlaceholder={hideAndroidPlaceholder}
                            customKeydownHandler={customKeydownHandler}
                            onFocus={onFocus}
                            onBlur={onBlur}
                        />
                        {showAndroidPlaceholder && (
                            <RichTextPlaceholder placeholder={placeholder} />
                        )}
                    </Box>
                    <OnFocusPlugin
                        autoFocus={autoFocus}
                        editor={editor}
                        onFocusChange={onFocusChange}
                    />
                    {!isEditing && (
                        <CaptureLinkAttachmentsPlugin onUpdate={onMessageLinksUpdated} />
                    )}
                    <RememberInputPlugin storageId={storageId.current} />
                    {!isEditing && sendButtons}
                </Stack>
                {unfurledLinkAttachments && unfurledLinkAttachments.length > 0 && (
                    <Box horizontal gap padding flexWrap="wrap" width="100%">
                        {unfurledLinkAttachments.map((attachment) => (
                            <UnfurledLinkAttachmentPreview
                                key={attachment.id}
                                attachment={attachment}
                                onRemove={onRemoveUnfurledLinkAttachment}
                            />
                        ))}
                    </Box>
                )}
                {tickerAttachments && tickerAttachments.length > 0 && (
                    <Box horizontal gap padding flexWrap="wrap" width="100%">
                        {tickerAttachments.map((attachment) => (
                            <TickerAttachmentPreview
                                key={attachment.id}
                                attachment={attachment}
                                onRemove={onRemoveTicker}
                            />
                        ))}
                    </Box>
                )}
                <Stack paddingX gap="xs" paddingBottom="sm">
                    <PasteFilePlugin
                        editableContainerRef={
                            editableContainerRef as React.RefObject<HTMLDivElement>
                        }
                    />

                    {isTouch && toolbarTop}

                    {showThreadCheckbox && (
                        <Stack
                            horizontal
                            justifyContent="start"
                            height={isTouch ? 'x4' : undefined}
                            alignItems="center"
                            onClick={handleRefocus}
                        >
                            <Checkbox
                                labelLast
                                small={!isTouch}
                                name="sendInThread"
                                checked={replyMode === 'thread'}
                                label={
                                    <Paragraph color="gray2">
                                        {hasThreadReplies ? 'Send to thread' : 'Start a thread'}
                                    </Paragraph>
                                }
                                onChange={(e) =>
                                    setReplyMode(e.target.checked ? 'thread' : 'inline')
                                }
                            />
                        </Stack>
                    )}
                    <Stack
                        gap
                        shrink
                        horizontal
                        flexWrap="wrap"
                        pointerEvents={editable ? 'auto' : 'none'}
                    >
                        <EditorToolbarBottom
                            editing={isEditing}
                            focused={focused}
                            threadId={threadId}
                            threadPreview={threadPreview}
                            visible={
                                (isTouch &&
                                    !isFormattingToolbarOpen &&
                                    (focused || !isEditorEmpty)) ||
                                (!isTouch && (focused || !isEditorEmpty))
                            }
                            isFormattingToolbarOpen={isFormattingToolbarOpen}
                            setIsFormattingToolbarOpen={setIsFormattingToolbarOpen}
                            key="toolbar"
                        />
                        <Box grow />
                        {isEditing && sendButtons}
                        {renderTradingBottomBar}
                    </Stack>
                </Stack>
            </Box>
        </Plate>
    )
}
